// @ts-check
import React, { useEffect } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Switch,
  useLocation,
} from 'react-router-dom';

// core
import { AppProvider, appRoles, routerUrl } from 'core';
// Place auth before components to satisfy import/order rule.
import { AuthProvider, ProtectedRoute } from 'core/auth';
import {
  AppStateAlert,
  ErrorBoundary,
  Footer,
  Navbar,
  NavSummary,
} from 'core/components';
import { FeatureFlagsProvider } from 'core/context/feature-flag-context';
// pages
import {
  Admin,
  Announcement,
  CampaignBasicData,
  CampaignDefineFilter,
  CampaignDetails,
  CampaignSearch,
  DevModeLogin,
  Home,
  LogOut,
  PageInProgress,
  PageNotAuthorized,
  PageNotFound,
  Support,
} from 'pages';
// styles
import './App.css';

// FeatureFlagsProvider needs AuthContext to know when user is logged in.
// AppProvider and other components can then use feature flags.
function AppPages() {
  const { pathname } = useLocation();

  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <AppProvider>
          <section className="app">
            <Navbar />
            <main className="container p-2">
              <ErrorBoundary resetKey={pathname}>
                <AppStateAlert />
                <NavSummary />
                <Switch>
                  <ProtectedRoute
                    exact
                    path={routerUrl.HOME}
                    component={Home}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.CAMPAIGN_SEARCH}
                    component={CampaignSearch}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.CAMPAIGN_SCHEDULE}
                    component={PageInProgress}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.REPORTING}
                    component={PageInProgress}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.SUPPORT}
                    component={Support}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.ADMIN}
                    component={Admin}
                    roles={[appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.ANNOUNCEMENT_VIEW_DATA}
                    component={Announcement}
                    roles={[appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      routerUrl.CAMPAIGN_UPDATE_DATA,
                      routerUrl.CAMPAIGN_VIEW_DATA,
                      routerUrl.CAMPAIGN_CREATE_DATA,
                    ]}
                    component={CampaignBasicData}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      routerUrl.CAMPAIGN_UPDATE_FILTER,
                      routerUrl.CAMPAIGN_VIEW_FILTER,
                      routerUrl.CAMPAIGN_DEFINE_FILTER,
                    ]}
                    component={CampaignDefineFilter}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <ProtectedRoute
                    path={routerUrl.CAMPAIGN_DETAILS}
                    component={CampaignDetails}
                    roles={[appRoles.CREATOR, appRoles.ADMIN]}
                  />
                  <Route path={routerUrl.LOG_OUT} component={LogOut} />
                  <Route
                    path={routerUrl.PAGE_NOT_AUTHORIZED}
                    component={PageNotAuthorized}
                  />
                  <Route
                    path={['*', routerUrl.PAGE_NOT_FOUND]}
                    component={PageNotFound}
                  />
                </Switch>
              </ErrorBoundary>
            </main>
            <Footer />
          </section>
        </AppProvider>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
}

function App() {
  useEffect(() => {
    const pushGAEvent = (event) => {
      const { dataLayer } = /** @type {any} */ (window);
      if (dataLayer) dataLayer.push(event);
    };

    const handleClick = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const el = target.closest('[data-ga-text]');
      if (!el) return;

      const dropdown = el.getAttribute('aria-expanded');
      const action = dropdown
        ? dropdown === 'false'
          ? 'open'
          : 'close'
        : el.getAttribute('data-ga-action');

      pushGAEvent({
        name: (el.getAttribute('data-ga-name') || '').toLowerCase(),
        event: (el.getAttribute('data-ga-event') || '').toLowerCase(),
        action: (action || '').toLowerCase(),
        type: (el.getAttribute('data-ga-type') || '').toLowerCase(),
        section: (el.getAttribute('data-ga-section') || '').trim(),
        region: (el.getAttribute('data-ga-region') || '').toLowerCase(),
        text: (el.getAttribute('data-ga-text') || '').trim(),
        component: (el.getAttribute('data-ga-component') || '').trim(),
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <Router>
      <Switch>
        <Route
          path={[routerUrl.DEV_LOGIN, `${routerUrl.DEV_LOGIN}/*`]}
          component={DevModeLogin}
        />
        <Route path="/" component={AppPages} />
      </Switch>
    </Router>
  );
}

export default App;
